import React, { useEffect, useRef, useState } from "react";
import { AiOutlineCloseCircle } from "react-icons/ai";
import Modal from './Modal'
import { API_BASE } from '../config'

export function AddDeviceModal({ isOpen, onClose, onSubmit }) {
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    type: "lift",
    status: "operational",
    details: "",
  });

  const [userInfo, setUserInfo] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // decode id_token to extract email
  function decodeIdTokenEmail() {
    try {
      const t = sessionStorage.getItem('id_token')
      if (!t) return null
      const payload = t.split('.')[1]
      const json = decodeURIComponent(atob(payload.replace(/-/g, '+').replace(/_/g, '/')).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }).join(''))
      const p = JSON.parse(json)
      return (p.email || '').toString().toLowerCase()
    } catch (e) {
      console.warn('failed to decode id_token', e)
      return null
    }
  }

  async function fetchUserRecord(email) {
    try {
      const token = sessionStorage.getItem('id_token')
      const url = `${API_BASE}/reg-user?emailId=${encodeURIComponent(email)}`
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!resp.ok) return null
      return await resp.json()
    } catch (e) {
      console.error('fetchUserRecord error', e)
      return null
    }
  }

  function decodeIdTokenUser() {
    try {
      const t = sessionStorage.getItem('id_token')
      if (!t) return null
      const payload = t.split('.')[1]
      const json = decodeURIComponent(atob(payload.replace(/-/g, '+').replace(/_/g, '/')).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }).join(''))
      return JSON.parse(json)
    } catch (e) {
      console.warn('failed to decode id_token user payload', e)
      return null
    }
  }

  useEffect(() => {
    if (isOpen) {
      const email = decodeIdTokenEmail()
      if (email) {
        fetchUserRecord(email).then(user => {
          if (user) {
            setUserInfo(user)
          }
        })
      }
    }
  }, [isOpen])

  /* ---------------- ESC key close ---------------- */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  /* ---------------- Click outside close ---------------- */
  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  /* ---------------- Validation ---------------- */
  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Device name is required";
    }
    if (!formData.type) newErrors.type = "Device type is required";
    if (!formData.status) newErrors.status = "Status is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* Generate Device ID */
  const generateDeviceId = () => {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `DEVICE_${timestamp}_${random}`;
  };

  /* Map device type to database value */
  const mapDeviceType = (type) => {
    const typeMapping = {
      'lift': 'ELEVATOR',
      'generator': 'GENERATOR',
      'Common Area Lighting': 'LIGHTING',
      'other': 'OTHER'
    };
    return typeMapping[type] || type;
  };

  /* ---------------- Submit ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (!userInfo) {
      alert("Unable to retrieve user information. Please try again.");
      return;
    }

    try {
      setLoading(true);

      const tokenUser = decodeIdTokenUser() || {}
      const communityId = userInfo?.COMMUNITY_ID || userInfo?.communityId || userInfo?.COMM_VISTRS_PRTN_KEY || sessionStorage.getItem('community_id') || tokenUser.communityId || tokenUser.COMMUNITY_ID || '';
      const userEmail = (userInfo?.EMAIL || userInfo?.email || tokenUser.email || tokenUser.preferred_username || sessionStorage.getItem('user_email') || '').toString().toLowerCase();
      const userId = userInfo?.UNIT_NBR || userInfo?.unitNbr || userInfo?.id || tokenUser.email || 'unknown';
      const role = userInfo?.ROLE || userInfo?.role || tokenUser.role || sessionStorage.getItem('user_role') || 'resident';
      const updatedBy = userEmail || `${userId}_${role.toLowerCase()}`;

      if (!communityId) {
        throw new Error('Missing community ID');
      }

      const payload = {
        COMMUNITY_ID: communityId,
        DEVICE_ID: generateDeviceId(),
        DEVICE_NAME: formData.name,
        DEVICE_TYPE: mapDeviceType(formData.type),
        STATUS: formData.status,
        UPDATED_BY: updatedBy,
        LAST_UPDATED: new Date().toISOString(),
      };

      console.log("Submitting new device payload:", payload);

      const token = sessionStorage.getItem('id_token');
      const response = await fetch(`${API_BASE}/devices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.text();
        throw new Error(`Failed to add device: ${errData}`);
      }

      const result = await response.json();
      console.log("Device created:", result);
      onSubmit(result);

      setFormData({
        name: "",
        type: "lift",
        status: "operational",
        details: "",
      });

      setErrors({});
      onClose();
    } catch (error) {
      console.error("Error adding device:", error);
      alert("Unable to add device. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Add Device" onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-4">
        <div className="space-y-4">
            {/* Device Name */}
            <div>
              <label className="block text-sm mb-1 text-gray-700">Device Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="e.g., Lift 1 - Tower A"
                disabled={loading}
              />
              {errors.name && (
                <p className="text-xs text-red-600 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Device Type */}
            <div>
              <label className="block text-sm mb-1 text-gray-700">Device Type *</label>
              <select
                required
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.type ? "border-red-500" : "border-gray-300"
                }`}
                disabled={loading}
              >
                <option value="generator">Common Area Lighting</option>
                <option value="lift">Lift/Elevator</option>
                <option value="generator">Generator</option>
                <option value="other">Other</option>
              </select>
              {errors.type && (
                <p className="text-xs text-red-600 mt-1">{errors.type}</p>
              )}
            </div>

            {/* Initial Status */}
            <div>
              <label className="block text-sm mb-1 text-gray-700">Initial Status *</label>
              <select
                required
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.status ? "border-red-500" : "border-gray-300"
                }`}
                disabled={loading}
              >
                <option value="operational">Operational</option>
                <option value="maintenance">Maintenance</option>
                <option value="offline">Offline</option>
              </select>
              {errors.status && (
                <p className="text-xs text-red-600 mt-1">{errors.status}</p>
              )}
            </div>

            {/* Details */}
            <div>
              <label className="block text-sm mb-1 text-gray-700">Details</label>
              <textarea
                value={formData.details}
                onChange={(e) =>
                  setFormData({ ...formData, details: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={3}
                placeholder="Additional information about the device"
                disabled={loading}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-4 py-2 rounded-lg text-white ${
                loading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {loading ? "Adding..." : "Add Device"}
            </button>
          </div>
      </form>
    </Modal>
  );
}
export default AddDeviceModal;